import React from 'react';
import { Box, Typography, Stack, Grid, Paper } from '@mui/material';
import { CrownIcon, TrendingUpIcon, CoinsIcon, CalculatorIcon } from './VipIcons'; 
import { useTranslation } from "react-i18next";

// Color Palette
const COLORS = {
  slate950: '#020617',
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate600: '#475569',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate300: '#cbd5e1',
  amber600: '#d97706',
  amber500: '#f59e0b',
  amber400: '#fbbf24',
  yellow400: '#facc15',
  emerald400: '#34d399',
  blue400: '#60a5fa',
  purple400: '#a855f7', // Added for Live Casino
  white: '#ffffff',
};

const VipCard = ({ tier, isHighest }) => {
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: process.env.REACT_APP_CURRENCY_CODE,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const { t } = useTranslation();
  const isBaseTier = tier.rank === 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        transition: 'transform 0.3s ease',
        cursor: 'default',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-8px)',
            '& .glow-effect': { opacity: 0.3 },
          },
        },
        ...(isHighest && {
          gridColumn: { md: 'span 2', lg: 'span 1' },
        }),
      }}
    >
      {/* Glow Effect */}
      <Box
        className="glow-effect"
        sx={{
          position: 'absolute',
          inset: '-2px',
          background: `linear-gradient(to right, ${COLORS.amber600}, ${COLORS.yellow400})`,
          borderRadius: '16px',
          opacity: 0,
          filter: 'blur(8px)',
          transition: 'opacity 0.3s',
          zIndex: 0,
        }}
      />

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          backgroundColor: COLORS.slate900,
          border: `1px solid ${COLORS.slate800}`,
          borderRadius: '16px',
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        {/* Top Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -16,
            right: -16,
            width: 96,
            height: 96,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)',
          }}
        />

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
<Box
              sx={{
                width: 56, 
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid',
                borderColor: isBaseTier ? COLORS.slate600 : COLORS.amber500,
                backgroundColor: isBaseTier ? COLORS.slate800 : 'rgba(120, 53, 15, 0.3)',
                overflow: 'hidden', 
                padding: '4px'      
              }}
            >
              {tier.logo ? (
                 <Box
                   component="img"
                   src={tier.logo}
                   alt={tier.displayName?.trim() || `VIP ${tier.rank}`}
                   sx={{
                     width: '100%',
                     height: '100%',
                     objectFit: 'contain'
                   }}
                 />
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: isBaseTier ? COLORS.slate400 : COLORS.amber400,
                    lineHeight: 1,
                  }}
                >
                  {tier.displayName?.trim() || tier.rank}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: isBaseTier ? COLORS.slate300 : COLORS.white,
                  lineHeight: 1.2,
                }}
              >
                {tier.displayName?.trim() || `VIP ${tier.rank}`}
              </Typography>
              {/* <Typography
                variant="caption"
                sx={{
                  color: COLORS.slate500,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                }}
              >
                {isBaseTier ? 'Member' : 'Elite Status'}
              </Typography> */}
            </Box>
          </Stack>

          {!isBaseTier && (
            <Box sx={{ color: 'rgba(245, 158, 11, 0.5)' }}>
              <CrownIcon />
            </Box>
          )}
        </Stack>

        {/* Deposit Info */}
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            backgroundColor: 'rgba(2, 6, 23, 0.5)',
            borderRadius: 2,
            border: `1px solid ${COLORS.slate800}`,
          }}
        >
          <Typography variant="caption" sx={{ color: COLORS.slate400, display: 'block', mb: 0.5 }}>
             {t("vipnew.requireddeposit")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.white }}>
            {tier.accumulatedDeposit > 0 ? formatCurrency(tier.accumulatedDeposit) : 'Free'}
          </Typography>
        </Box>

        <Typography 
            variant="caption" 
            sx={{ 
                color: COLORS.slate400, 
                fontWeight: 600, 
                textTransform: 'uppercase', 
                letterSpacing: '0.05em',
                mb: 1,
                display: 'block' 
            }}
            >
            {t("vipnew.rebatebonus")}
         </Typography>

        {/* Stats Grid - CHANGED TO 3 COLUMNS */}
        <Grid container spacing={1} sx={{ mb: 3 }}>
      
          {/* 1. Slots Rebate */}
          <Grid item xs={4}>
            <Box sx={statBoxStyles}>
              <Box sx={{ color: COLORS.emerald400, mb: 0.5 }}>
                <TrendingUpIcon style={{ width: 16, height: 16 }} />
              </Box>
              <Typography variant="caption" sx={statLabelStyles}>
                {t("vipnew.slots")}
              </Typography>
              <Typography variant="body2" sx={statValueStyles}>
                {tier.attributes.slotsRebatePercentage.toFixed(2)}%
              </Typography>
            </Box>
          </Grid>

          {/* 2. Sports Rebate */}
          <Grid item xs={4}>
            <Box sx={statBoxStyles}>
              <Box sx={{ color: COLORS.blue400, mb: 0.5 }}>
                <TrendingUpIcon style={{ width: 16, height: 16 }} />
              </Box>
              <Typography variant="caption" sx={statLabelStyles}>
                 {t("vipnew.sports")}
              </Typography>
              <Typography variant="body2" sx={statValueStyles}>
                {tier.attributes.sportsRebatePercentage.toFixed(2)}%
              </Typography>
            </Box>
          </Grid>

          {/* 3. Live Casino Rebate - NEW ADDITION */}
          <Grid item xs={4}>
            <Box sx={statBoxStyles}>
              <Box sx={{ color: COLORS.purple400, mb: 0.5 }}>
                {/* Reusing TrendingUpIcon or use Calculator/Card icon if you have one */}
                <TrendingUpIcon style={{ width: 16, height: 16 }} />
              </Box>
              <Typography variant="caption" sx={statLabelStyles}>
                 {t("vipnew.casino")}
              </Typography>
              <Typography variant="body2" sx={statValueStyles}>
                {tier.attributes.liveCasinoRebatePercentage?.toFixed(2) || '0.00'}%
              </Typography>
            </Box>
          </Grid>

        </Grid>

        {/* Footer */}
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            borderTop: `1px solid ${COLORS.slate800}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: COLORS.amber500, display: 'flex' }}>
              <CoinsIcon style={{ width: 20, height: 20 }} />
            </Box>
            <Typography variant="body2" sx={{ color: COLORS.slate300 }}>
              {t("vipnew.rankupgradebonus")}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLORS.amber400 }}>
            {tier.upgradeBonus > 0 ? formatCurrency(tier.upgradeBonus) : '-'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// Reusable Styles for the 3 grid items to keep code clean
const statBoxStyles = {
  backgroundColor: 'rgba(30, 41, 59, 0.5)', // slate-800/50
  p: 1,
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  height: '100%',
  justifyContent: 'center'
};

const statLabelStyles = {
  color: COLORS.slate400, 
  fontSize: '0.65rem', // Slightly smaller to fit 3 columns
  lineHeight: 1.1,
  marginBottom: '2px'
};

const statValueStyles = {
  fontWeight: 'bold', 
  color: COLORS.white,
  fontSize: '0.875rem'
};

export default VipCard;